/**
 * Default page javascript. All other pages extend this one.
 */

//imports
import "lazysizes";
import "core-js/modules/es.promise";
//bootstrap imports
import "bootstrap/js/affix";
import "bootstrap/js/alert";
import "bootstrap/js/button";
import "bootstrap/js/carousel";
import "bootstrap/js/collapse";
import "bootstrap/js/dropdown";
import "bootstrap/js/modal";
import "bootstrap/js/scrollspy";
import "bootstrap/js/tab";
import "bootstrap/js/tooltip";
import "bootstrap/js/transition";

// Polyfill
import arrayFindPolyfill from "./arrayFind";

// Libs
import aosInit from "./aos";
import whenDomReady from "when-dom-ready";
import setIndicationCookie from "./setIndicationCookie";
import btnClickSetIndicationCookie from "./btnClickSetIndicationCookie";
import siteConfig from "./siteConfig";
import extend from "./extend";
import LoadScript from "../plugins/loadScript";
import signupForm from "./signupForm";
import documentMailer from "./documentMailer";
import References from "./references";
import BtnCollapsible from "./btnCollapsible";
import ImageComparisonSlider from "./imageComparisonSlider";
import CostCoverageTool from "./costCoverageTool";
import HeroTextAnimate from "./heroTextAnimate";
import TakeQuiz from "./takeQuiz";
import chart from "./chart";
import FilterGalleryForm from "./filterGalleryForm";
import FullPageTabs from "./fullPageTabs";
import EmailDocModalSubmit from "./emailDocModalSubmit";
import CarouselIndications from "./carouselIndications";
import Webinar from "./webinar";
import WebinarForm from "./webinarForm";
import createMOC from "./createMOC";
import GhostSignup from "./ghostSignupForm";
import videoGalleryTranscriptScroll from "./videoGalleryTranscriptScroll";
import patientStoriesAmbassador from "./patientStoriesAmbassador";
import carouselInit from "./carousel";
import ddgTooltip from "./ddgTooltip";
import ddgSliders from "./ddgSliders";
import indicationListDropdownBand from "./indicationDropdownBand/indicationDropdownBand";
import ddgInit from "./ddg/ddgMain";
import flipToggleInit from "./flipToggle/flipToggleMain";
import slideQuiz from "./slideQuiz";
import flipCard from "./flipCard";
import novaVideoThumbnail from "./sections/videoThumbnail";
import novaCarousel from "./sections/carousel";
import novaPodcastPlayer from "./sections/podcastPlayer";
import imageCarouselHero from "./imageCarouselHero";
import "./autocompleteAlgolia";
import video from "./video";
import ViewTranscript from "./viewTranscript.js";
import isiInformation from "../stores/isiInformation";
import isi from "./isi";
import reminders from "./reminders";
import "./searchBoxV2/searchBoxV2.js";
import initTippy from "./tippy/index.js";

//Core Libs
import CoreIsiSticky from "./coreIsiSticky";
import CoreEventRegistration from "./coreEventRegistration";
import CoreEventCancellation from "./coreEventCancellation";
import CoreGlossaryTooltips from "./coreGlossaryTooltips";
import CoreInterstitials from "./coreInterstitials";
import coreStickyHeader from "./coreStickyHeader";
import coreHeader from "./coreHeader";

import accessibilityInit from "./accessibility";

// Styles
import "../../css/pages/BasePage.scss";

//alpine
import AsyncAlpine from "async-alpine";
import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import mask from "@alpinejs/mask";
import "../magics/loadScript";
import vimeoPlayer from "./video/vimeo";
import scrollGradient from "./scrollGradient";
import "../directives/swipe/index";
import "../directives/lightbox/index";
import "../directives/validator/index";
import "../directives/tabs/index";
import getProp from "../magics/getProp";
import audienceController from "./audienceController";
import audienceReactiveContent from "../directives/audienceReactiveContent";

class BasePage {
    constructor(...opts) {
        extend(this, opts);
        window.Alpine = Alpine;
        Alpine.plugin(collapse);
        Alpine.plugin(mask);
        Alpine.plugin(AsyncAlpine);
        Alpine.asyncData("flipCard", () => import("./flipCard"));
        Alpine.asyncData("vimeoAudioPlayer", () => import("./audio/index.js"));
        Alpine.asyncData("damAudioPlayer", () => import("./audio/damAudio.js"));
        Alpine.asyncData("carousel", () => import("./carousel/index.js"));
        Alpine.asyncData("totalDropdown", () => import("./totalDropdown"));
        Alpine.asyncData("quickPollSelection", () => import("./quickPoll/quickPollSelection.js"));
        Alpine.asyncData("survey", () => import("./survey/survey.js"));
        Alpine.asyncData("eoeAwarenessForm", () => import("./eoeAwareness/eoeAwarenessForm.js"));
        Alpine.asyncData("eoeAwarenessModal", () => import("./eoeAwarenessModal/eoeAwarenessModal.js"));
        Alpine.asyncData("copayCTATracking", () => import("./copayTrackingGA/ctaTracking.js"));
        Alpine.asyncData("docFinder", () => import("./docFinder.js"));
        Alpine.asyncData("form", () => import("./form/form.js"));
        Alpine.asyncData("doctorDiscussionChecklist", () => import("./doctorDiscussionChecklist"));
        Alpine.asyncData("doctorDiscussionChecklistResults", () => import("./doctorDiscussionChecklistResults"));
        Alpine.asyncData("selectDropdown", () => import("../../../../sanofi-dupixent/js/modules/alpine/selectDropdown/index.js"));
        Alpine.asyncData("mainNavigationAd", () => import("./mainNavigationAd/mainNavigationAd.js"));
        Alpine.asyncData("sdmQuestionnaire", () => import("./sdmQuestionnaire.js"));
        Alpine.asyncData("sdmQuestionnaireResults", () => import("./sdmQuestionnaireResults.js"));
        Alpine.asyncData("genericVideoGallery", () => import("./genericVideoGallery/index.js"));
        Alpine.bind("audienceReactiveContent", audienceReactiveContent);
        Alpine.magic("getProp", getProp);
        Alpine.data("audienceController", audienceController);
        Alpine.data("vimeoPlayer", vimeoPlayer);
        Alpine.data("scrollGradient", scrollGradient);
        Alpine.store("isiInformation", isiInformation);
        Alpine.data("isi", isi);
        Alpine.start();
        this.init();
    }

    init() {
        const self = this;
        self.ready = false;
        self.onBeforeReady();

        whenDomReady().then(function() {
            self.onReady();
        });

        window.addEventListener("load", function() {
            self.onLoad();
        });
    }

    onBeforeReady() {
        extend(this, siteConfig);
        arrayFindPolyfill();
        createMOC.init();
        setIndicationCookie.init();
        btnClickSetIndicationCookie.init();
        CoreIsiSticky.init();
        new LoadScript();
    }

    onReady() {
        const self = this;
        self.ready = true;
        new References();
        new signupForm();
        new BtnCollapsible();
        new ImageComparisonSlider();
        new CostCoverageTool();
        documentMailer();
        new TakeQuiz();
        new FilterGalleryForm();
        new FullPageTabs();
        new CoreEventRegistration();
        new CoreEventCancellation();
        new ViewTranscript();
        new Webinar();
        new WebinarForm();
        indicationListDropdownBand();
        aosInit.aosInit();
        initTippy();
    }

    onLoad() {
        document.getElementsByTagName("body")[0].classList.add("loaded");
        new HeroTextAnimate();
        new chart();
        CarouselIndications.init();
        EmailDocModalSubmit.init();
        video.init();
        videoGalleryTranscriptScroll.init();
        CoreInterstitials.init();
        CoreGlossaryTooltips.init();
        coreStickyHeader();
        coreHeader();
        GhostSignup.init();
        ddgTooltip();
        ddgSliders.sliderFunct();
        ddgSliders.selectOnlyNone();
        patientStoriesAmbassador.init();
        carouselInit();
        ddgInit();
        flipToggleInit();
        accessibilityInit();
        slideQuiz();
        flipCard();
        novaVideoThumbnail();
        novaCarousel();
        novaPodcastPlayer();
        imageCarouselHero();
        reminders();
    }

    isReady() {
        var self = this;
        return self.ready === true;
    }

    noop() {}
}

export default BasePage;
